import { ref, update, query, orderByChild, equalTo, get } from 'firebase/database';
import { releaseBookingSlots, promoteBookingSlots, claimBookingSlots } from './bookingAvailabilityUtil';
import { releaseEquipmentRental, promoteEquipmentRental } from './equipmentAvailabilityUtil';

/**
 * Multi-date bookings (see App.jsx's createPendingBooking) are stored as one
 * Firebase record PER DATE under `bookings/`, each with its own `date` /
 * `timeSlots` / slotLocks+equipmentHolds claims — exactly like a normal
 * single-date booking — but sharing the same `confirmationId` and a
 * `groupId` (the first date-record's own key). This keeps every existing
 * per-date query (`orderByChild('date')`), the slotLocks/equipmentHolds
 * transaction helpers, and admin screens like AdminEquipment working
 * completely unchanged, since each record is still a normal, self-contained
 * single-date booking. The helpers below are what stitch those sibling
 * records back together (or cascade an action across all of them) wherever
 * a screen needs to treat the group as one logical reservation.
 *
 * A single-date booking has no `groupId` at all — every helper here treats
 * that as "a group of one" for full backward compatibility with existing
 * data and any code that isn't group-aware.
 */

/**
 * Collapses a flat list of booking records (each `{ id, ...data }`, as read
 * straight off a Firebase snapshot) into one combined entry per logical
 * reservation. Records sharing a `groupId` are merged into a single entry
 * carrying every field from the group's primary record (or the lone record,
 * for a single-date booking) plus:
 *   - `dates`: [{ date, timeSlots }, ...], chronologically sorted
 *   - `memberIds`: this group's underlying Firebase record ids, index-aligned with `dates`
 *
 * @param {Array<Object>} records
 * @returns {Array<Object>}
 */
export const groupBookingRecords = (records) => {
  const byGroup = new Map();
  const singles = [];

  (records || []).forEach(rec => {
    if (!rec) return;
    if (rec.groupId) {
      if (!byGroup.has(rec.groupId)) byGroup.set(rec.groupId, []);
      byGroup.get(rec.groupId).push(rec);
    } else {
      singles.push(rec);
    }
  });

  const grouped = [];
  byGroup.forEach((members) => {
    const sorted = [...members].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const primary = sorted.find(m => m.groupPrimary) || sorted[0];
    grouped.push({
      ...primary,
      id: primary.id,
      dates: sorted.map(m => ({ date: m.date, timeSlots: m.timeSlots })),
      memberIds: sorted.map(m => m.id),
    });
  });

  const singleEntries = singles.map(rec => ({
    ...rec,
    dates: [{ date: rec.date, timeSlots: rec.timeSlots }],
    memberIds: [rec.id],
  }));

  return [...grouped, ...singleEntries];
};

/**
 * One-shot fetch of every sibling record sharing `groupId` (including the
 * primary), sorted chronologically by date — used wherever a screen only
 * has ONE member of a multi-date group in hand (e.g. an admin clicked one
 * cell in the day grid) but needs to act on the whole reservation. Returns
 * `{ dates, memberIds }`, already in the shape releaseGroupClaims /
 * promoteGroupClaims / updateGroupStatus expect. Falls back to a
 * single-member group (just `fallbackId`/`fallbackDate`) if `groupId` is
 * falsy, so callers can use this unconditionally regardless of whether the
 * booking in hand turns out to be part of a group.
 *
 * Also returns `records` — the full `{ id, ...data }` of every member it
 * actually read — for callers that need a FIELD off the group rather than
 * just its ids. The partial-payment flow needs it: only the primary
 * record's `paymentAmount` is the reservation's grand total. It comes back
 * empty on the fallback paths, where nothing was read.
 *
 * @param {Object} db
 * @param {string|null|undefined} groupId
 * @param {string} fallbackId - record id to use when there's no groupId.
 * @param {string} fallbackDate - that record's own date.
 */
export const fetchGroupMembers = async (db, groupId, fallbackId, fallbackDate) => {
  if (!db || !groupId) {
    return { dates: [{ date: fallbackDate }], memberIds: [fallbackId], records: [] };
  }
  const bookingsRef = ref(db, 'bookings');
  const q = query(bookingsRef, orderByChild('groupId'), equalTo(groupId));
  const snapshot = await get(q);
  const members = [];
  if (snapshot.exists()) {
    snapshot.forEach(child => { members.push({ id: child.key, ...child.val() }); });
  }
  if (members.length === 0) {
    return { dates: [{ date: fallbackDate }], memberIds: [fallbackId], records: [] };
  }
  const sorted = members.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  return {
    dates: sorted.map(m => ({ date: m.date, timeSlots: m.timeSlots })),
    memberIds: sorted.map(m => m.id),
    records: sorted,
  };
};

/**
 * Sets `status` (plus any extra fields) on every date-record in a group —
 * or just the one record, for a single-date booking — via a single
 * multi-path Firebase update, so a status change (confirm/decline/cancel)
 * always applies to the whole reservation at once rather than just
 * whichever date happened to be clicked.
 *
 * @param {Object} db
 * @param {string[]} memberIds - this group's record ids (see groupBookingRecords).
 * @param {string} status
 * @param {Object} [extra] - additional { field: value } pairs to set on every member.
 */
export const updateGroupStatus = async (db, memberIds, status, extra = {}) => {
  if (!db || !memberIds || memberIds.length === 0) return;
  const updates = {};
  memberIds.forEach(id => {
    if (!id) return;
    updates[`bookings/${id}/status`] = status;
    Object.entries(extra).forEach(([key, value]) => {
      updates[`bookings/${id}/${key}`] = value;
    });
  });
  await update(ref(db), updates);
};

/**
 * Releases slotLocks + equipmentHolds for every date in a group. `dates`
 * and `memberIds` must be index-aligned (see groupBookingRecords) — each
 * date-record's own record id is what was used to claim its own slots, so
 * each has to be released with its own (date, id) pair.
 */
export const releaseGroupClaims = async (db, dates, memberIds) => {
  if (!db || !dates || !memberIds) return;
  await Promise.all(dates.map((d, i) => {
    const id = memberIds[i];
    if (!d?.date || !id) return Promise.resolve();
    return Promise.all([
      releaseBookingSlots(db, d.date, id).catch(() => {}),
      releaseEquipmentRental(db, d.date, id).catch(() => {}),
    ]);
  }));
};

/**
 * Firebase returns an array-like `timeSlots` value as either a real array or
 * an object keyed by index, depending on how it was written. claimBookingSlots
 * iterates it as an array, so normalize before handing it over.
 * @param {Object} timeSlots
 * @returns {Object} { timeSlot: number[] }
 */
const normalizeTimeSlots = (timeSlots) => {
  const out = {};
  Object.entries(timeSlots || {}).forEach(([timeSlot, courts]) => {
    out[timeSlot] = Array.isArray(courts) ? courts.map(Number) : Object.values(courts || {}).map(Number);
  });
  return out;
};

/**
 * Re-claims a group's slots for a fresh payment window, for a booking being
 * submitted AFTER its payment countdown already lapsed.
 *
 * This exists because a lapsed 'awaiting_payment' booking is recoverable by
 * design — BookingStatusScreen tells the guest "this reservation is being
 * held for you" and lets them upload a receipt whenever they get back. But
 * once the countdown passes, the slotLocks entry is expired, so anyone else
 * can legitimately take that slot in the meantime. Promoting the old claim
 * blindly would then leave TWO live bookings on one court-hour.
 *
 * claimBookingSlots is one all-or-nothing write per date that the rules refuse
 * if another live claim holds the slot, so this is a genuine atomic re-check
 * rather than a read-then-write another guest can slip between. A partial
 * success is rolled back, so the caller either holds every date or none.
 *
 * The claim is TIMED, not open-ended: the rules only let a guest hold a slot
 * indefinitely once the booking is 'pending'. The caller promotes it with
 * promoteGroupClaims right after the status flips.
 *
 * @param {Object} db
 * @param {Array<{date: string, timeSlots: Object}>} dates
 * @param {string[]} memberIds - index-aligned with `dates`.
 * @param {string} expiresAt - ISO deadline for the re-claim (now + payment timeout).
 * @returns {Promise<{granted: boolean, conflicts: Array<{date: string, timeSlot: string, court: number}>}>}
 */
export const reclaimGroupClaims = async (db, dates, memberIds, expiresAt) => {
  if (!db || !dates?.length || !memberIds?.length) return { granted: false, conflicts: [] };

  const claimed = [];
  const conflicts = [];

  for (let i = 0; i < dates.length; i += 1) {
    const entry = dates[i];
    const id = memberIds[i];
    if (!entry?.date || !id) continue;
    // eslint-disable-next-line no-await-in-loop
    const result = await claimBookingSlots(db, entry.date, normalizeTimeSlots(entry.timeSlots), id, expiresAt);
    if (result.granted) {
      claimed.push({ date: entry.date, id });
    } else {
      result.conflicts.forEach(c => conflicts.push({ date: entry.date, ...c }));
      break; // one taken date is enough — the whole reservation can't stand
    }
  }

  if (conflicts.length > 0) {
    // Undo the dates that did succeed, so a failed recovery doesn't leave
    // this booking silently holding half a reservation.
    await Promise.all(claimed.map(c => releaseBookingSlots(db, c.date, c.id).catch(() => {})));
    return { granted: false, conflicts };
  }
  return { granted: true, conflicts: [] };
};

/**
 * Promotes (clears the payment-countdown expiry on) slotLocks +
 * equipmentHolds for every date in a group — mirrors releaseGroupClaims.
 */
export const promoteGroupClaims = async (db, dates, memberIds) => {
  if (!db || !dates || !memberIds) return;
  await Promise.all(dates.map((d, i) => {
    const id = memberIds[i];
    if (!d?.date || !id) return Promise.resolve();
    return Promise.all([
      promoteBookingSlots(db, d.date, id).catch(() => {}),
      promoteEquipmentRental(db, d.date, id).catch(() => {}),
    ]);
  }));
};
